import {
  Injectable,
  computed,
  DestroyRef,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Dialog } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { firstValueFrom } from 'rxjs';
import {
  CABINET_DIALOG,
  CABINET_PATHS,
  CABINET_QR_SCAN,
  CABINET_STAFF_CLIENT_QR_PRINT,
  formatOrderShortPublicId,
  getClientOrderPreferredScheduleAt,
  getClientOrderScheduleAt,
  getStaffOrderKindLabelKeyForRow,
  getStaffOrderLogisticsLabelKeys,
} from '@nebalkon-workspace/cabinet-core';
import {
  OrderApiService,
  type OrderWalletStatementEntry,
  type OrderOperationalContext,
  OrderResponse,
  OrderStatuses,
  type OrderSupportedAction,
  OrderStorageItem,
  OrderStorageLine,
  OrderStorageResponse,
  OrderTypes,
  PatchOrderRequest,
} from '@nebalkon-workspace/order-api';
import {
  BoxService,
  BoxSizeService,
  ItemResponse,
  ItemService,
  UpdateStaffItemRequest,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import type { WarehouseResponse } from '@nebalkon-workspace/storage-api';
import type { BoxSizeResponse } from '@nebalkon-workspace/storage-api';
import {
  OrderPhotoItem,
  PhotoUploadService,
} from '@nebalkon-workspace/media-api';
import { AuthService, ProfileService, UserRole } from '@nebalkon-workspace/auth';
import {
  EmployeeAdminService,
  EmployeeResponse,
  AdminUserItemResponse,
  UserAdminService,
} from '@nebalkon-workspace/admin-api';
import {
  AcceptanceActDocxService,
  downloadAcceptanceActForOrderIntake,
  downloadReturnActForOrderHandover,
  isClientProfileVerified,
  ITEM_MAX_PARTS_COUNT,
  ITEM_MAX_QUANTITY,
  ITEM_MIN_PARTS_COUNT,
  ITEM_MIN_QUANTITY,
  itemStatusLabelKey,
  normalizeItemQuantity,
  resolveIntakeItemsForReceive,
  ReturnActDocxService,
} from '@nebalkon-workspace/cabinet-client';
import type { ProfileWithUserResponse } from '@nebalkon-workspace/auth';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { StaffOrderEditDialogComponent } from '../staff-order-edit-dialog.component';
import type { StaffOrderEditDialogClose } from '../staff-order-edit-dialog.types';
import { StaffOrderCatalogPickerDialogComponent } from '../staff-order-catalog-picker-dialog.component';
import type { StaffOrderCatalogPickerDialogClose } from '../staff-order-catalog-picker-dialog.types';
import {
  computeDeliveryComplete,
  computeHandoverProgress,
  computeMoverAcceptProgress,
  computeReceiveProgress,
  MOVER_ACCEPTED_STORAGE_STATUS,
  orderCompositionLineKind,
  orderCompositionLineLabel,
  orderCompositionStorageStatusKey,
  orderStorageItemLabel,
  orderLineAcceptBoxId,
  orderLineIsMoverAccepted,
  orderLineItemInBox,
  orderStorageHasContent,
} from '../staff-order-composition.util';
import { StaffUserItemDetailModalComponent } from '../../admin/staff-user-item-detail-modal.component';
import { printStaffEntityQr } from '../../utils/staff-entity-qr-print.util';
import { resolveOwnerLastName } from '../../utils/resolve-owner-last-name.util';
import type { StaffQrLabelEntityKind } from '../../utils/staff-qr-label-type.util';
import {
  supportedOrderActions,
  type StaffOrderActionBlockReason,
  type StaffOrderActionGroups,
  type StaffOrderActionKind,
  type StaffOrderActionsResult,
  type StaffOrderOperationalBlocker,
  type StaffOrderOperationalProgress,
  type StaffOrderOperationalScenarioId,
  type StaffOrderOperationalPhaseId,
  type StaffOrderSupportedAction,
} from '../staff-order-supported-actions.model';
import type {
  StaffOrderActionId,
  StaffOrderStaffRole,
} from '../staff-order-current-flow.model';
import { StaffOrderDetailContextService } from '../staff-order-detail-context.service';
import {
  resolveOrderReceiveCode,
  type ResolveOrderReceiveCodeFailureReason,
} from '../../utils/resolve-order-receive-code.util';

const walletStatementPageSize = 5;

@Injectable()
export class StaffOrderDetailFacade {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #api = inject(OrderApiService);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #auth = inject(AuthService);
  readonly #profile = inject(ProfileService);
  readonly #employees = inject(EmployeeAdminService);
  readonly #boxService = inject(BoxService);
  readonly #boxSizes = inject(BoxSizeService);
  readonly #photoUpload = inject(PhotoUploadService);
  readonly #warehouseService = inject(WarehouseService);
  readonly #dialog = inject(CABINET_DIALOG);
  readonly #qrScan = inject(CABINET_QR_SCAN);
  readonly #orderContext = inject(StaffOrderDetailContextService);
  readonly #orderEditDialog = inject(Dialog);
  readonly #userAdmin = inject(UserAdminService);
  readonly #itemService = inject(ItemService);
  readonly #acceptanceAct = inject(AcceptanceActDocxService);
  readonly #returnAct = inject(ReturnActDocxService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #printer = inject(NiimbotPrinterService);
  readonly #printClientQr = inject(CABINET_STAFF_CLIENT_QR_PRINT, {
    optional: true,
  });
  readonly #document = inject(DOCUMENT);
  readonly #platformId = inject(PLATFORM_ID);

  readonly formatOrderShortPublicId = formatOrderShortPublicId;
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly staffCabinetPath = this.#cabinetPaths.staff;

  readonly loading = signal(true);
  readonly savingMode = signal<
    | 'idle'
    | 'status'
    | 'note'
    | 'assign'
    | 'mover'
    | 'receive'
    | 'handover'
    | 'confirmVisit'
    | 'box'
    | 'item'
    | 'release'
    | 'orderItems'
    | 'removeItem'
    | 'accept'
    | 'assignToBox'
  >('idle');

  readonly saving = computed(() => this.savingMode() !== 'idle');
  readonly errorKind = signal<'load' | 'notFound' | 'accessDenied' | null>(
    null,
  );
  readonly row = signal<OrderResponse | null>(null);

  readonly newNoteText = signal('');

  readonly receivingMode = signal(false);
  readonly receiveManualCode = signal('');
  readonly printingClientQr = signal(false);
  readonly clientProfile = signal<ProfileWithUserResponse | null>(null);
  readonly clientProfileLoading = signal(false);
  readonly handoverManualCode = signal('');
  readonly receiveWarehouseInfo = signal<WarehouseResponse | null>(null);
  readonly receiveWarehouseLoading = signal(false);
  readonly warehouseInfo = signal<WarehouseResponse | null>(null);
  readonly warehouseLoading = signal(false);
  readonly moverCandidates = signal<EmployeeResponse[]>([]);
  readonly selectedMoverIds = signal<string[]>([]);
  readonly leadMoverId = signal<string | null>(null);
  readonly assignmentReason = signal('');

  /** Коробки и вещи заказа (мувер). */
  readonly inventoryLoading = signal(false);
  readonly storage = signal<OrderStorageResponse | null>(null);
  readonly boxSizeOptions = signal<BoxSizeResponse[]>([]);
  readonly selectedBoxSizeId = signal<string>('');
  readonly newItemBoxId = signal<string>('');
  readonly newItemName = signal('');
  readonly newItemPhoto = signal<File | null>(null);
  readonly newItemIsDisassembled = signal(false);
  readonly newItemPartsCount = signal('');
  readonly newItemQuantity = signal('');
  readonly canSubmitNewMoverItem = computed(() => {
    if (!this.newItemBoxId() || !this.newItemName().trim()) {
      return false;
    }
    const quantity = normalizeItemQuantity(this.newItemQuantity());
    if (quantity < ITEM_MIN_QUANTITY || quantity > ITEM_MAX_QUANTITY) {
      return false;
    }
    if (!this.newItemIsDisassembled()) {
      return true;
    }
    const partsCount = Number(this.newItemPartsCount().trim());
    return (
      Number.isFinite(partsCount) &&
      partsCount >= ITEM_MIN_PARTS_COUNT &&
      partsCount <= ITEM_MAX_PARTS_COUNT
    );
  });
  /** Выбор коробки для переноса каталожной вещи (ключ — id строки order_items). */
  readonly moverLineBoxSelection = signal<Record<string, string>>({});

  /** Фото заказа (клиент / гость). */
  readonly orderPhotos = signal<OrderPhotoItem[]>([]);
  readonly orderPhotosLoading = signal(false);

  readonly userCatalogItems = signal<AdminUserItemResponse[]>([]);
  readonly userCatalogItemsLoading = signal(false);

  readonly itemStatusLabelKey = itemStatusLabelKey;
  readonly orderCompositionLineLabel = orderCompositionLineLabel;
  readonly orderStorageItemLabel = orderStorageItemLabel;
  readonly orderCompositionLineKind = orderCompositionLineKind;

  readonly isManager = computed(() => {
    const roles = this.#auth.roles();
    return roles.includes(UserRole.Admin) || roles.includes(UserRole.Manager);
  });

  readonly isMover = computed(() =>
    this.#auth.roles().includes(UserRole.Mover),
  );

  readonly isWarehouseWorker = computed(() =>
    this.#auth.roles().includes(UserRole.WarehouseWorker),
  );

  readonly canAssignMovers = computed(() => {
    const r = this.row();
    if (!r || r.client_self_dropoff || r.client_self_pickup) {
      return false;
    }
    return (
      this.isManager() &&
      (r.status === OrderStatuses.Pending ||
        r?.status === OrderStatuses.InWork ||
        r?.status === OrderStatuses.InTransit)
    );
  });

  readonly assignMoversRequiresReason = computed(() => {
    const status = this.row()?.status;
    return (
      status === OrderStatuses.InWork || status === OrderStatuses.InTransit
    );
  });

  readonly canSubmitAssign = computed(() => {
    const ids = this.selectedMoverIds();
    const lead = this.leadMoverId();
    return (
      ids.length > 0 &&
      lead != null &&
      ids.includes(lead) &&
      (!this.assignMoversRequiresReason() ||
        this.assignmentReason().trim().length > 0) &&
      !this.saving()
    );
  });

  readonly canEditOrder = computed(() => {
    const r = this.row();
    if (!r || !this.isManager()) {
      return false;
    }
    return (
      r.status !== OrderStatuses.Completed &&
      r.status !== OrderStatuses.Canceled
    );
  });

  readonly showWalletCard = computed(() => !!this.row()?.user_id);

  readonly walletSummary = computed(() => this.row()?.wallet_summary ?? null);
  readonly #walletStatementEntries = signal<OrderWalletStatementEntry[]>([]);
  readonly walletStatement = computed(() => {
    const entries = this.#walletStatementEntries();
    if (entries.length > 0) {
      return entries;
    }
    return this.row()?.wallet_statement ?? [];
  });
  readonly walletStatementLoading = signal(false);
  readonly #walletStatementExhausted = signal(false);
  readonly #walletStatementNextOffset = signal(0);
  readonly canLoadMoreWalletStatement = computed(
    () =>
      this.showWalletCard() &&
      this.walletStatement().length > 0 &&
      !this.walletStatementLoading() &&
      !this.#walletStatementExhausted(),
  );

  readonly timeline = computed(() => this.row()?.timeline ?? []);

  readonly canAddNote = computed(() => {
    const r = this.row();
    return (
      this.isManager() &&
      r?.status !== OrderStatuses.New &&
      this.newNoteText().trim().length > 0
    );
  });

  readonly canManageOrderItems = computed(() => {
    const r = this.row();
    return (
      this.isManager() &&
      !!r?.user_id &&
      r.status !== OrderStatuses.New &&
      r.status !== OrderStatuses.Completed &&
      r.status !== OrderStatuses.Canceled
    );
  });

  readonly showManagerOrderComposition = computed(
    () => this.isManager() && !!this.row()?.user_id,
  );

  readonly orderCompositionHasContent = computed(() =>
    orderStorageHasContent(this.storage()),
  );

  readonly canMoverStart = computed(() => {
    const r = this.row();
    return this.isMover() && r?.status === OrderStatuses.Pending;
  });

  readonly showMoverTransitButton = computed(() => {
    const r = this.row();
    if (!this.isMover() || !r) {
      return false;
    }
    if (r.type === OrderTypes.Moving) {
      return false;
    }
    if (r.status === OrderStatuses.InWork) {
      return r.type === OrderTypes.Pickup || r.type === OrderTypes.Delivery;
    }
    if (
      r.status === OrderStatuses.InTransit &&
      r.type === OrderTypes.Delivery &&
      r.return_to_warehouse
    ) {
      return true;
    }
    return false;
  });

  readonly moverPickupAcceptGateActive = computed(() => {
    const r = this.row();
    return (
      this.isMover() &&
      r?.type === OrderTypes.Pickup &&
      r.status === OrderStatuses.InWork
    );
  });

  readonly moverAcceptProgress = computed(() =>
    computeMoverAcceptProgress(this.storage()),
  );

  readonly canSubmitMoverTransit = computed(() => {
    if (!this.showMoverTransitButton()) {
      return false;
    }
    if (!this.moverPickupAcceptGateActive()) {
      return true;
    }
    if (this.inventoryLoading() || !this.storage()) {
      return false;
    }
    const progress = this.moverAcceptProgress();
    if (!progress || progress.total === 0) {
      return false;
    }
    return progress.accepted === progress.total;
  });

  /** Hint only after storage is loaded and some units are still not accepted. */
  readonly showMoverTransitBlockedHint = computed(() => {
    if (
      !this.moverPickupAcceptGateActive() ||
      this.inventoryLoading() ||
      !this.storage()
    ) {
      return false;
    }
    const progress = this.moverAcceptProgress();
    if (!progress || progress.total === 0) {
      return false;
    }
    return progress.accepted < progress.total;
  });

  readonly showMoverOrderLines = computed(
    () => this.isMover() && this.canMoverInventory(),
  );

  readonly showOrderWorkspace = computed(() => {
    if (this.isMover()) {
      return (
        this.canMoverStart() ||
        this.showMoverTransitButton() ||
        this.canMoverDelivered() ||
        this.showMoverOrderLines() ||
        this.canMoverInventory()
      );
    }
    if (this.isManager()) {
      return this.showManagerOrderComposition();
    }
    if (this.isWarehouseWorker()) {
      return this.canWarehouseReceive() && this.receivingMode();
    }
    return false;
  });

  /** Создание коробок/вещей на объекте: после «Приступить». */
  readonly canMoverInventory = computed(() => {
    const r = this.row();
    if (!this.isMover() || !r?.user_id) {
      return false;
    }
    if (
      r.status !== OrderStatuses.InWork &&
      r.status !== OrderStatuses.InTransit
    ) {
      return false;
    }
    return (
      r.type === OrderTypes.Pickup ||
      r.type === OrderTypes.Moving ||
      r.type === OrderTypes.Delivery
    );
  });

  readonly moverTransitLabelKey = computed(() => {
    const r = this.row();
    if (!r) {
      return 'admin.storageRequests.transitToWarehouse';
    }
    if (
      r.status === OrderStatuses.InTransit &&
      r.type === OrderTypes.Delivery &&
      r.return_to_warehouse
    ) {
      return 'admin.storageRequests.transitToWarehouse';
    }
    if (r.type === OrderTypes.Delivery) {
      return 'admin.storageRequests.transitToClient';
    }
    return 'admin.storageRequests.transitToWarehouse';
  });

  readonly canMoverDelivered = computed(() => {
    const r = this.row();
    return (
      this.isMover() &&
      r?.type === OrderTypes.Delivery &&
      !r.return_to_warehouse &&
      r.status === OrderStatuses.InTransit
    );
  });

  readonly canWarehouseReceive = computed(() => {
    const r = this.row();
    return (
      this.isWarehouseWorker() &&
      r?.status === OrderStatuses.InTransit &&
      !!r.warehouse_id
    );
  });

  readonly receiveProgress = computed(() =>
    computeReceiveProgress(this.storage()),
  );

  readonly receiveBlockedByUnverifiedProfile = computed(() => {
    const order = this.row();
    if (!order?.user_id) {
      return false;
    }
    if (this.clientProfileLoading()) {
      return true;
    }
    return !isClientProfileVerified(this.clientProfile());
  });

  readonly handoverProgress = computed(() => {
    const r = this.row();
    return computeHandoverProgress(this.storage(), {
      countStoredAsResolved:
        !!r?.client_self_pickup && !!r?.return_to_warehouse,
    });
  });

  readonly deliveryComplete = computed(() =>
    computeDeliveryComplete(this.storage()),
  );

  readonly isClientSelfLogistics = computed(() => {
    const r = this.row();
    return !!r?.client_self_dropoff || !!r?.client_self_pickup;
  });

  readonly canConfirmClientVisit = computed(() => {
    const action = this.orderActions()?.byRole.manager?.find(
      (entry) => entry.id === 'confirm_client_visit',
    );
    return !!action?.available;
  });

  readonly warehousePanelMode = computed((): 'receive' | 'handover' | null => {
    const r = this.row();
    if (!this.isWarehouseWorker() || !r?.warehouse_id) {
      return null;
    }
    if (r.client_self_pickup && r.status === OrderStatuses.InTransit) {
      return 'handover';
    }
    if (
      r.status === OrderStatuses.InTransit &&
      (r.type === OrderTypes.Pickup ||
        (r.type === OrderTypes.Delivery && r.return_to_warehouse))
    ) {
      return 'receive';
    }
    return null;
  });

  readonly canWarehouseCreateQrBox = computed(() => {
    const r = this.row();
    return (
      this.isWarehouseWorker() &&
      this.warehousePanelMode() === 'receive' &&
      !!r?.user_id &&
      !!r.warehouse_id &&
      this.boxSizeOptions().length > 0
    );
  });

  readonly orderActions = computed((): StaffOrderActionsResult | null => {
    const r = this.row();
    if (!r) {
      return null;
    }
    if (r.operational) {
      return mapBackendOrderActions(r.operational);
    }
    return supportedOrderActions({
      order: r,
      roles: this.#auth.roles(),
      progress: {
        moverAccept: this.moverAcceptProgress(),
        warehouseReceive: this.receiveProgress(),
        warehouseHandover: this.handoverProgress(),
        deliveryComplete: this.deliveryComplete(),
      },
    });
  });

  readonly #managerWorkspaceAvailable = computed(() => {
    const r = this.row();
    if (!this.isManager() || !r) {
      return false;
    }
    return this.canAssignMovers() || this.showManagerOrderComposition();
  });

  readonly #moverWorkspaceAvailable = computed(() => {
    if (!this.isMover() || this.isClientSelfLogistics()) {
      return false;
    }
    return (
      this.canMoverStart() ||
      this.showMoverTransitButton() ||
      this.canMoverDelivered() ||
      this.showMoverOrderLines() ||
      this.canMoverInventory()
    );
  });

  readonly #warehouseWorkspaceAvailable = computed(
    () => this.warehousePanelMode() !== null,
  );

  readonly activeWorkspaceRole = computed<StaffOrderStaffRole | null>(() => {
    const actions = this.orderActions();

    const isRoleWorkspaceAvailable = (role: StaffOrderStaffRole): boolean => {
      switch (role) {
        case 'manager':
          return this.#managerWorkspaceAvailable();
        case 'mover':
          return this.#moverWorkspaceAvailable();
        case 'warehouse_worker':
          return this.#warehouseWorkspaceAvailable();
      }
    };

    const rolePriority: StaffOrderStaffRole[] = [
      'manager',
      'warehouse_worker',
      'mover',
    ];

    const primaryRole = actions?.cockpitPrimaryAction?.role;
    if (primaryRole && isRoleWorkspaceAvailable(primaryRole)) {
      return primaryRole;
    }

    for (const role of rolePriority) {
      const nextByRole = actions?.primaryNextByRole?.[role];
      if (nextByRole?.available && isRoleWorkspaceAvailable(role)) {
        return role;
      }
    }

    for (const role of rolePriority) {
      if (isRoleWorkspaceAvailable(role)) {
        return role;
      }
    }

    return null;
  });

  /** Cockpit: show only active role workspace for current stage. */
  readonly showManagerPanel = computed(
    () => this.activeWorkspaceRole() === 'manager',
  );

  /** Cockpit: show only active role workspace for current stage. */
  readonly showMoverPanel = computed(
    () => this.activeWorkspaceRole() === 'mover',
  );

  /** Cockpit: show only active role workspace for current stage. */
  readonly showWarehousePanel = computed(
    () => this.activeWorkspaceRole() === 'warehouse_worker',
  );

  readonly referenceLinksExpanded = signal(false);
  readonly attachmentsExpanded = signal(false);
  readonly timelineExpanded = signal(false);
  readonly detailsExpanded = signal(false);

  /** Отдать вещь клиенту (только delivery + return). */
  readonly canReleaseItemsToClient = computed(() => {
    const r = this.row();
    return (
      this.isMover() &&
      r?.type === OrderTypes.Delivery &&
      r.return_to_warehouse &&
      (r.status === OrderStatuses.InWork ||
        r.status === OrderStatuses.InTransit)
    );
  });

  toggleReferenceLinksExpanded(): void {
    this.referenceLinksExpanded.update((expanded) => !expanded);
  }

  toggleAttachmentsExpanded(): void {
    this.attachmentsExpanded.update((expanded) => !expanded);
  }

  toggleTimelineExpanded(): void {
    this.timelineExpanded.update((expanded) => !expanded);
  }

  toggleDetailsExpanded(): void {
    this.detailsExpanded.update((expanded) => !expanded);
  }

  /** Executes cockpit primary/secondary action from supported-actions model. */
  dispatchCockpitAction(actionId: StaffOrderActionId): void {
    switch (actionId) {
      case 'take_in_progress':
        void this.takeInProgress();
        break;
      case 'agree_pending':
        void this.agreePending();
        break;
      case 'confirm_client_visit':
        void this.confirmClientVisit();
        break;
      case 'cancel':
        void this.cancelRequest();
        break;
      case 'complete':
        void this.completeRequest();
        break;
      case 'edit_order':
        void this.openEditOrderDialog();
        break;
      case 'manage_order_items':
        this.openCatalogPicker();
        break;
      case 'mover_start':
        this.moverStart();
        break;
      case 'mover_transit':
        this.moverTransit();
        break;
      case 'mover_delivered':
        this.moverDelivered();
        break;
      case 'warehouse_receive':
        void this.scanReceiveQr();
        break;
      case 'warehouse_handover':
        void this.scanHandoverQr();
        break;
      case 'assign_movers':
      case 'add_note':
      case 'mover_accept_box':
      case 'mover_accept_item':
      case 'mover_inventory':
      case 'release_items_to_client':
        this.focusActiveWorkspace();
        break;
    }
  }

  focusActiveWorkspace(): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }
    const workspace = this.#document.getElementById(
      'staff-order-active-workspace',
    );
    if (!workspace) {
      return;
    }
    workspace.scrollIntoView({ behavior: 'smooth', block: 'start' });
    workspace.focus({ preventScroll: true });
  }

  formatPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 9) {
      return `+375${digits}`;
    }
    return phone;
  }

  displayOrDash(value: string | number | undefined | null): string {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    return String(value);
  }

  pricingEstimateDeclaredVolume(r: OrderResponse): number | null {
    return r.pricing_estimate?.declared_volume_m3 ?? null;
  }

  pricingEstimateNowAmount(r: OrderResponse): number | null {
    const lines = r.pricing_estimate?.lines;
    if (!lines?.length) {
      return null;
    }
    const total = lines
      .filter(
        (line) =>
          line.applicable &&
          line.id !== 'future_return' &&
          line.amount_bynd >= 0,
      )
      .reduce((sum, line) => sum + line.amount_bynd, 0);
    return total > 0 ? total : 0;
  }

  formatBynd(amount: number): string {
    const rounded = Math.round(amount * 100) / 100;
    return `${rounded
      .toFixed(2)
      .replace(/(\.\d*?)0+$/, '$1')
      .replace(/\.$/, '')
      .replace('.', ',')} руб.`;
  }

  formatVolumeM3(volume: number): string {
    const rounded = Math.round(volume * 1000) / 1000;
    return rounded
      .toFixed(3)
      .replace(/(\.\d*?)0+$/, '$1')
      .replace(/\.$/, '')
      .replace('.', ',');
  }

  warehouseDisplay(r: OrderResponse): string {
    const wh = this.warehouseInfo();
    if (wh?.name?.trim()) {
      const id = r.warehouse_id?.trim();
      return id ? `${wh.name} (${id})` : wh.name;
    }
    return this.displayOrDash(r.warehouse_id);
  }

  onOpenClientProfile(): void {
    const id = this.row()?.user_id;
    if (!id) {
      return;
    }
    void this.#router.navigate([this.staffCabinetPath, 'users', id]);
  }

  onOpenClientItems(): void {
    const id = this.row()?.user_id;
    if (!id) {
      return;
    }
    void this.#router.navigate([this.staffCabinetPath, 'users', id, 'items']);
  }

  onCreateClientItem(): void {
    const id = this.row()?.user_id;
    if (!id) {
      return;
    }
    void this.#router.navigate([this.staffCabinetPath, 'users', id, 'items'], {
      queryParams: { create: 1 },
    });
  }

  onOpenClientOrders(): void {
    const id = this.row()?.user_id;
    if (!id) {
      return;
    }
    void this.#router.navigate([this.staffCabinetPath, 'orders'], {
      queryParams: { user_id: id },
    });
  }

  public destroy(): void {
    this.#orderContext.clear();
  }

  public init(): void {
    if (this.isManager()) {
      this.#loadMoverCandidates();
    }

    this.#route.paramMap
      .pipe(
        map((params) => params.get('id')),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((id) => {
        this.#loadOrderDetail(id);
      });
  }

  #loadMoverCandidates(): void {
    this.#employees.getEmployees().subscribe({
      next: (res) => {
        const movers = res.data.filter(
          (e) => e.is_active && e.user.roles.includes(UserRole.Mover),
        );
        this.moverCandidates.set(movers);
      },
      error: () => {
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [
            this.#translate.instant('admin.storageRequests.employeesLoadError'),
          ],
        });
      },
    });
  }

  #loadOrderDetail(id: string | null): void {
    this.#orderContext.clear();
    this.errorKind.set(null);
    this.row.set(null);
    this.storage.set(null);
    this.clientProfile.set(null);
    this.clientProfileLoading.set(false);
    this.#walletStatementEntries.set([]);

    if (!id) {
      this.errorKind.set('notFound');
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.walletStatementLoading.set(false);
    this.#walletStatementExhausted.set(false);
    this.#walletStatementNextOffset.set(0);

    this.#api
      .getOrder(id, {
        wallet_limit: walletStatementPageSize,
        wallet_offset: 0,
      })
      .subscribe({
        next: (data) => {
          if (this.#route.snapshot.paramMap.get('id') !== id) {
            return;
          }

          this.row.set(data);
          if (data.user_id) {
            this.#loadClientProfile(data.user_id);
          } else {
            this.clientProfile.set(null);
            this.clientProfileLoading.set(false);
          }
          const initialStatement = data.wallet_statement ?? [];
          this.#walletStatementEntries.set(initialStatement);
          this.#walletStatementExhausted.set(
            initialStatement.length < walletStatementPageSize,
          );
          this.#walletStatementNextOffset.set(initialStatement.length);
          this.#orderContext.setUserId(data.user_id);
          this.#applyMoversFromOrder(data);
          this.#loadOrderPhotos(id);
          if (data.warehouse_id) {
            this.#loadWarehouseInfo(data.warehouse_id);
          } else {
            this.warehouseInfo.set(null);
          }
          this.loading.set(false);
          if (this.isMover() && data.user_id) {
            this.#loadBoxSizes();
            this.#loadInventory(id);
          }
          if (this.isManager() && data.user_id) {
            this.#loadInventory(id);
          }
          if (
            this.isWarehouseWorker() &&
            data.status === OrderStatuses.InTransit &&
            data.warehouse_id
          ) {
            if (data.user_id) {
              this.#loadBoxSizes();
            }
            this.#loadInventory(id);
            this.#loadReceiveWarehouseLabel(data.warehouse_id);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (this.#route.snapshot.paramMap.get('id') !== id) {
            return;
          }

          this.loading.set(false);
          this.row.set(null);
          this.#walletStatementEntries.set([]);
          this.#walletStatementNextOffset.set(0);
          if (err.status === 403) {
            this.errorKind.set('accessDenied');
          } else if (err.status === 404) {
            this.errorKind.set('notFound');
          } else {
            this.errorKind.set('load');
          }
        },
      });
  }

  loadMoreWalletStatement(): void {
    const currentOrder = this.row();
    if (
      !currentOrder ||
      !this.showWalletCard() ||
      this.walletStatementLoading() ||
      this.#walletStatementExhausted()
    ) {
      return;
    }

    const currentStatement = this.walletStatement();
    if (
      this.#walletStatementEntries().length === 0 &&
      currentStatement.length > 0
    ) {
      this.#walletStatementEntries.set(currentStatement);
    }

    let offset = this.#walletStatementNextOffset();
    if (offset <= 0 && currentStatement.length > 0) {
      offset = currentStatement.length;
      this.#walletStatementNextOffset.set(offset);
    }
    this.walletStatementLoading.set(true);
    this.#api
      .getOrder(currentOrder.id, {
        wallet_limit: walletStatementPageSize,
        wallet_offset: offset,
      })
      .subscribe({
        next: (data) => {
          if (this.row()?.id !== currentOrder.id) {
            this.walletStatementLoading.set(false);
            return;
          }

          this.row.set(data);
          const nextChunk = data.wallet_statement ?? [];
          if (nextChunk.length > 0) {
            this.#walletStatementEntries.update((current) => {
              const existingIds = new Set(current.map((entry) => entry.id));
              const uniqueNext = nextChunk.filter(
                (entry) => !existingIds.has(entry.id),
              );
              return uniqueNext.length > 0
                ? [...current, ...uniqueNext]
                : current;
            });
          }
          this.#walletStatementExhausted.set(
            nextChunk.length < walletStatementPageSize,
          );
          this.#walletStatementNextOffset.set(offset + nextChunk.length);
          this.walletStatementLoading.set(false);
        },
        error: () => {
          this.walletStatementLoading.set(false);
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'error',
            text: [
              this.#translate.instant(
                'staff.orders.cockpit.wallet.statement.loadError',
              ),
            ],
          });
        },
      });
  }

  displayTitle(r: OrderResponse): string {
    const name = r.first_name?.trim();
    if (name) {
      return name;
    }
    const labelKeys = getStaffOrderLogisticsLabelKeys(r);
    if (labelKeys?.long) {
      return this.#translate.instant(labelKeys.long);
    }
    return this.#translate.instant('admin.storageRequests.displayNameFallback');
  }

  statusLabelKey(status: string): string {
    const map: Record<string, string> = {
      [OrderStatuses.New]: 'admin.storageRequests.orderStatusNew',
      [OrderStatuses.InProcess]: 'admin.storageRequests.orderStatusInProcess',
      [OrderStatuses.Pending]: 'admin.storageRequests.orderStatusPending',
      [OrderStatuses.InWork]: 'admin.storageRequests.orderStatusInWork',
      [OrderStatuses.InTransit]: 'admin.storageRequests.orderStatusInTransit',
      [OrderStatuses.Stored]: 'admin.storageRequests.orderStatusStored',
      [OrderStatuses.Completed]: 'admin.storageRequests.filterOrderCompleted',
      [OrderStatuses.Canceled]: 'admin.storageRequests.filterOrderCancelled',
    };
    return map[status] ?? 'admin.storageRequests.orderStatusNew';
  }

  orderTypeLabel(r: OrderResponse): string {
    return getStaffOrderKindLabelKeyForRow(r);
  }

  agreedScheduleAt(r: OrderResponse): string | undefined {
    return getClientOrderScheduleAt(r);
  }

  preferredScheduleAt(r: OrderResponse): string | undefined {
    return getClientOrderPreferredScheduleAt(r);
  }

  showPreferredSchedulePending(r: OrderResponse): boolean {
    return !this.agreedScheduleAt(r) && Boolean(this.preferredScheduleAt(r));
  }

  paymentLabel(ps: string): string {
    const map: Record<string, string> = {
      pending: 'admin.storageRequests.paymentStatusPending',
      paid: 'admin.storageRequests.paymentStatusPaid',
      overdue: 'admin.storageRequests.paymentStatusOverdue',
      failed: 'admin.storageRequests.paymentStatusFailed',
    };
    return map[ps] ?? ps;
  }

  sourceLabelKey(source: string): string {
    const map: Record<string, string> = {
      mobile: 'admin.storageRequests.sourceApp',
      web: 'admin.storageRequests.sourceWeb',
      admin: 'admin.storageRequests.sourceAdmin',
      unknown: 'admin.storageRequests.sourceUnknown',
    };
    return map[source] ?? 'admin.storageRequests.sourceUnknown';
  }

  statusBadgeClass(status: string): Record<string, boolean> {
    return {
      'request-status': true,
      'request-status--pending': status === OrderStatuses.New,
      'request-status--waiting': status === OrderStatuses.Pending,
      'request-status--progress':
        status === OrderStatuses.InProcess ||
        status === OrderStatuses.InWork ||
        status === OrderStatuses.InTransit,
      'request-status--done': status === OrderStatuses.Stored,
      'request-status--completed': status === OrderStatuses.Completed,
      'request-status--cancelled': status === OrderStatuses.Canceled,
    };
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('common.clipboardCopyError')],
      });
    }
  }

  toggleMoverSelection(id: string): void {
    const cur = this.selectedMoverIds();
    if (cur.includes(id)) {
      const next = cur.filter((x) => x !== id);
      this.selectedMoverIds.set(next);
      if (this.leadMoverId() === id) {
        this.leadMoverId.set(next[0] ?? null);
      }
    } else {
      const next = [...cur, id];
      this.selectedMoverIds.set(next);
      if (this.leadMoverId() == null) {
        this.leadMoverId.set(id);
      }
    }
  }

  isMoverSelected(id: string): boolean {
    return this.selectedMoverIds().includes(id);
  }

  setLeadMover(id: string): void {
    if (!this.selectedMoverIds().includes(id)) {
      this.selectedMoverIds.set([...this.selectedMoverIds(), id]);
    }
    this.leadMoverId.set(id);
  }

  submitAssignMovers(): void {
    const r = this.row();
    if (!r || !this.canSubmitAssign()) {
      return;
    }
    const ids = this.selectedMoverIds();
    const lead = this.leadMoverId();
    if (!lead || ids.length === 0) {
      return;
    }
    this.savingMode.set('assign');
    const body = {
      movers: ids.map((mid) => ({
        mover_id: mid,
        is_lead: mid === lead,
      })),
    };
    const reason = this.assignmentReason().trim();
    this.#api
      .assignMovers(r.id, reason ? { ...body, reason } : body)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.assignmentReason.set('');
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.assignMoversSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  employeeDisplayName(e: EmployeeResponse): string {
    const u = e.user;
    const ind = e.individual;
    const parts = [ind.first_name, ind.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (parts) {
      return parts;
    }
    return u.phone;
  }

  /** Resolve mover UUID to name when employee list is loaded (managers). */
  moverDisplayName(moverId: string): string {
    const e = this.moverCandidates().find((x) => x.user.id === moverId);
    if (e) {
      return this.employeeDisplayName(e);
    }
    return moverId;
  }

  #applyMoversFromOrder(data: OrderResponse): void {
    const movers = data.movers;
    if (!this.isManager() || !movers?.length) {
      return;
    }
    this.selectedMoverIds.set(movers.map((m) => m.mover_id));
    const lead = movers.find((m) => m.is_lead);
    this.leadMoverId.set(lead?.mover_id ?? movers[0].mover_id);
  }

  async takeInProgress(): Promise<void> {
    const ok = await this.#dialog.confirm({
      title: this.#translate.instant(
        'admin.storageRequests.takeInProgressTitle',
      ),
      message: this.#translate.instant(
        'admin.storageRequests.takeInProgressMessage',
      ),
      okLabel: this.#translate.instant('common.actions.ok'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!ok) {
      return;
    }
    this.#patchStatus(OrderStatuses.InProcess);
  }

  async agreePending(): Promise<void> {
    const ok = await this.#dialog.confirm({
      title: this.#translate.instant('admin.storageRequests.agreePendingTitle'),
      message: this.#translate.instant(
        'admin.storageRequests.agreePendingMessage',
      ),
      okLabel: this.#translate.instant('common.actions.ok'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!ok) {
      return;
    }
    this.#patchStatus(OrderStatuses.Pending);
  }

  async confirmClientVisit(): Promise<void> {
    const r = this.row();
    if (!r) {
      return;
    }
    const ok = await this.#dialog.confirm({
      title: this.#translate.instant(
        'admin.storageRequests.confirmClientVisitTitle',
      ),
      message: this.#translate.instant(
        r.client_self_pickup
          ? 'admin.storageRequests.confirmClientVisitMessagePickup'
          : 'admin.storageRequests.confirmClientVisitMessageDropoff',
      ),
      okLabel: this.#translate.instant(
        'admin.storageRequests.confirmClientVisitOk',
      ),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!ok) {
      return;
    }
    this.savingMode.set('confirmVisit');
    this.#api
      .confirmClientVisit(r.id)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          this.#loadInventory();
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.confirmClientVisitSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  async completeRequest(): Promise<void> {
    const r = this.row();
    if (!r) {
      return;
    }
    if (!r.user_id) {
      await this.#dialog.alert({
        title: this.#translate.instant(
          'admin.storageRequests.anonymousWarningTitle',
        ),
        message: this.#translate.instant(
          'admin.storageRequests.anonymousWarningMessage',
        ),
        okLabel: this.#translate.instant('common.actions.ok'),
      });
      return;
    }
    const ok = await this.#dialog.confirm({
      title: this.#translate.instant(
        'admin.storageRequests.completeOrderTitle',
      ),
      message: this.#translate.instant(
        'admin.storageRequests.completeOrderMessage',
      ),
      okLabel: this.#translate.instant('common.actions.ok'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!ok) {
      return;
    }
    this.#completeOrder();
  }

  async cancelRequest(): Promise<void> {
    const prompt = await this.#dialog.prompt({
      title: this.#translate.instant('admin.storageRequests.cancelReasonTitle'),
      message: this.#translate.instant(
        'admin.storageRequests.cancelReasonHint',
      ),
      inputPlaceholder: this.#translate.instant(
        'admin.storageRequests.cancelReasonPlaceholder',
      ),
      okLabel: this.#translate.instant('admin.storageRequests.cancelConfirm'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (prompt.cancelled) {
      return;
    }
    this.#cancelOrder(prompt.value.trim());
  }

  async openEditOrderDialog(): Promise<void> {
    const r = this.row();
    if (!r || !this.canEditOrder()) {
      return;
    }

    const ref = this.#orderEditDialog.open(StaffOrderEditDialogComponent, {
      data: { order: r },
      panelClass: 'cabinet-dialog-panel',
    });
    const result = (await firstValueFrom(
      ref.closed,
    )) as StaffOrderEditDialogClose;
    if (!result) {
      return;
    }

    this.#applyOrderUpdate(result.order);
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [this.#translate.instant('admin.storageRequests.editOrderSuccess')],
    });
  }

  addNote(): void {
    const r = this.row();
    if (!r || !this.canAddNote() || this.saving()) {
      return;
    }
    const note = this.newNoteText().trim();
    if (!note) {
      return;
    }
    this.#patchOrder(
      { note },
      'admin.storageRequests.noteAddedSuccess',
      true,
      'note',
    );
  }

  moverStart(): void {
    const r = this.row();
    if (!r || !this.canMoverStart()) {
      return;
    }
    this.savingMode.set('mover');
    this.#api
      .moverStart(r.id)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          if (updated.user_id) {
            this.#loadInventory();
          }
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.statusSavedSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  moverTransit(): void {
    const r = this.row();
    if (!r || !this.canSubmitMoverTransit()) {
      return;
    }
    this.savingMode.set('mover');
    this.#api
      .moverTransit(r.id)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          if (updated.user_id) {
            this.#loadInventory();
          }
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.statusSavedSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  moverDelivered(): void {
    const r = this.row();
    if (!r || !this.canMoverDelivered()) {
      return;
    }
    this.savingMode.set('mover');
    this.#api
      .moverDelivered(r.id)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          if (updated.user_id) {
            this.#loadInventory();
          }
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant('admin.storageRequests.deliveredSuccess'),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  toggleReceivingMode(): void {
    this.receivingMode.update((v) => {
      const next = !v;
      if (next) {
        const r = this.row();
        if (this.isWarehouseWorker() && r?.warehouse_id) {
          if (!this.storage()) {
            this.#loadInventory();
          }
          this.#loadReceiveWarehouseLabel(r.warehouse_id);
        }
      }
      return next;
    });
  }

  storageStatusLabel(status: string): string {
    const key = orderCompositionStorageStatusKey(status);
    return key.startsWith('admin.') ? this.#translate.instant(key) : status;
  }

  compositionLineStatusLabel(line: OrderStorageLine): string {
    const inv = this.storage();
    if (inv) {
      const boxId = orderLineAcceptBoxId(line, inv);
      if (boxId) {
        const box = inv.boxes.find((entry) => entry.box_id === boxId);
        if (box) {
          return this.storageStatusLabel(box.status);
        }
      }
    }
    if (line.item_status) {
      return this.storageStatusLabel(line.item_status);
    }
    return '—';
  }

  orderLineItemInBox(line: OrderStorageLine): boolean {
    const inv = this.storage();
    return inv ? orderLineItemInBox(line, inv) : false;
  }

  orderLineIsMoverAccepted(line: OrderStorageLine): boolean {
    const inv = this.storage();
    return inv ? orderLineIsMoverAccepted(line, inv) : false;
  }

  orderLineAcceptBoxId(line: OrderStorageLine): string | null {
    const inv = this.storage();
    return inv ? orderLineAcceptBoxId(line, inv) : null;
  }

  moverLineSelectedBoxId(lineId: string): string {
    return this.moverLineBoxSelection()[lineId] ?? '';
  }

  onMoverLineBoxChange(lineId: string, boxId: string): void {
    this.moverLineBoxSelection.update((current) => ({
      ...current,
      [lineId]: boxId,
    }));
  }

  isBoxMoverAccepted(boxId: string): boolean {
    const box = this.storage()?.boxes.find((entry) => entry.box_id === boxId);
    return box?.status === MOVER_ACCEPTED_STORAGE_STATUS;
  }

  async printMoverQr(
    entityId: string,
    entityKind: Extract<StaffQrLabelEntityKind, 'box' | 'item'>,
  ): Promise<void> {
    const caption = await resolveOwnerLastName(
      this.#userAdmin,
      this.row()?.user_id,
      this.clientProfile()?.individual?.last_name,
    );
    await printStaffEntityQr({
      entityId,
      entityKind,
      caption,
      printer: this.#printer,
      translate: this.#translate,
      notification: this.#notification,
    });
  }

  async printClientQr(): Promise<void> {
    const userId = this.row()?.user_id?.trim();
    const printClientQr = this.#printClientQr;
    if (!userId || !printClientQr || this.printingClientQr()) {
      return;
    }

    this.printingClientQr.set(true);
    try {
      const lastName = await resolveOwnerLastName(
        this.#userAdmin,
        userId,
        this.clientProfile()?.individual?.last_name,
      );
      await printClientQr({ userId, lastName });
    } finally {
      this.printingClientQr.set(false);
    }
  }

  moverAcceptItem(itemId: string): void {
    const r = this.row();
    if (!r || this.saving()) {
      return;
    }
    this.savingMode.set('accept');
    this.#api
      .moverAcceptItem(r.id, { item_id: itemId })
      .pipe(switchMap(() => this.#api.getOrderStorage(r.id)))
      .subscribe({
        next: (storage) => {
          this.storage.set(storage);
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant('admin.moverInventory.acceptSuccess'),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err, 'admin.moverInventory.acceptError');
        },
      });
  }

  moverAcceptBox(boxId: string): void {
    const r = this.row();
    if (!r || this.saving()) {
      return;
    }
    this.savingMode.set('accept');
    this.#api
      .moverAcceptBox(r.id, { box_id: boxId })
      .pipe(switchMap(() => this.#api.getOrderStorage(r.id)))
      .subscribe({
        next: (storage) => {
          this.storage.set(storage);
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant('admin.moverInventory.acceptSuccess'),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err, 'admin.moverInventory.acceptError');
        },
      });
  }

  assignMoverLineToBox(line: OrderStorageLine): void {
    const r = this.row();
    const itemId = line.item_id;
    const boxId = this.moverLineSelectedBoxId(line.id);
    if (!r?.user_id || !itemId || !boxId || this.saving()) {
      return;
    }
    this.savingMode.set('assignToBox');
    this.#boxService
      .addItemToBox(boxId, {
        user_id: r.user_id,
        item_id: itemId,
      })
      .pipe(switchMap(() => this.#api.getOrderStorage(r.id)))
      .subscribe({
        next: (storage) => {
          this.storage.set(storage);
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.moverInventory.assignToBoxSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err, 'admin.moverInventory.assignToBoxError');
        },
      });
  }

  openCatalogPicker(): void {
    const r = this.row();
    const userId = r?.user_id;
    if (!userId || !this.canManageOrderItems() || this.saving()) {
      return;
    }

    const ref = this.#orderEditDialog.open<
      StaffOrderCatalogPickerDialogClose,
      unknown,
      StaffOrderCatalogPickerDialogComponent
    >(StaffOrderCatalogPickerDialogComponent, {
      data: {
        orderId: r.id,
        userId,
        storage: this.storage(),
      },
      panelClass: 'staff-order-catalog-picker-panel',
    });

    ref.closed.subscribe((result) => {
      if (result?.added) {
        this.#loadInventory();
        this.#loadUserCatalogItems(userId).subscribe();
      }
    });
  }

  async openOrderCompositionLine(
    line: OrderStorageLine,
    context: 'default' | 'warehouse_receive' = 'default',
  ): Promise<void> {
    const itemId = line.item_id;
    if (!itemId) {
      return;
    }
    await this.#openOrderItemModal(itemId, context);
  }

  async openOrderCompositionBoxItem(
    item: OrderStorageItem,
    context: 'default' | 'warehouse_receive' = 'default',
  ): Promise<void> {
    await this.#openOrderItemModal(item.id, context);
  }

  async #openOrderItemModal(
    itemId: string,
    context: 'default' | 'warehouse_receive',
  ): Promise<void> {
    const r = this.row();
    const userId = r?.user_id;
    if (!userId) {
      return;
    }

    const item = await this.#resolveItemForModal(userId, itemId);
    if (!item) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            'admin.storageRequests.orderItemsItemLoadError',
          ),
        ],
      });
      return;
    }

    const canManage = this.canManageOrderItems();
    const canWarehouseEdit =
      context === 'warehouse_receive' && this.isWarehouseWorker();
    const ref = this.#orderEditDialog.open(StaffUserItemDetailModalComponent, {
      data: {
        item,
        context,
        updating: false,
        editable: canManage || canWarehouseEdit,
        ownerLastName: this.clientProfile()?.individual?.last_name,
        warehouseId: r?.warehouse_id,
        onChangeStatus: (nextStatus: string): Promise<void> =>
          this.#changeOrderItemStatus(userId, itemId, nextStatus),
        onSaveItem:
          canManage || canWarehouseEdit
            ? (body: UpdateStaffItemRequest): Promise<void> =>
                this.#saveOrderItem(userId, itemId, body)
            : undefined,
        onDeleteItem:
          canManage
            ? (): Promise<void> => this.#deleteOrderCatalogItem(userId, itemId)
            : undefined,
        onItemRefreshed: () => {
          this.#loadUserCatalogItems(userId).subscribe();
        },
      },
      panelClass: 'client-item-detail-modal-panel',
    });

    ref.closed.subscribe((changed) => {
      if (changed) {
        this.#loadInventory(r.id);
        this.#loadUserCatalogItems(userId).subscribe();
      }
    });
  }

  async removeOrderCompositionLine(line: OrderStorageLine): Promise<void> {
    const r = this.row();
    if (!r || !this.canManageOrderItems() || this.saving()) {
      return;
    }

    const ok = await this.#dialog.confirm({
      title: this.#translate.instant(
        'admin.storageRequests.orderItemsRemoveTitle',
      ),
      message: this.#translate.instant(
        'admin.storageRequests.orderItemsRemoveMessage',
        { name: this.orderCompositionLineLabel(line) },
      ),
      okLabel: this.#translate.instant(
        'admin.storageRequests.orderItemsRemove',
      ),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!ok) {
      return;
    }

    this.savingMode.set('removeItem');
    this.#api
      .removeOrderItem(r.id, line.id)
      .pipe(switchMap(() => this.#api.getOrderStorage(r.id)))
      .subscribe({
        next: (storage) => {
          if (this.row()?.id !== r.id) {
            this.savingMode.set('idle');
            return;
          }

          this.storage.set(storage);
          if (r.user_id) {
            this.#loadUserCatalogItems(r.user_id).subscribe();
          }
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.orderItemsRemoveSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(
            err,
            'admin.storageRequests.orderItemsRemoveError',
          );
        },
      });
  }

  async #changeOrderItemStatus(
    userId: string,
    itemId: string,
    nextStatus: string,
  ): Promise<void> {
    await firstValueFrom(
      this.#itemService.updateItemStatus(itemId, {
        new_status: nextStatus,
        reason: this.#translate.instant('admin.users.items.statusChangeReason'),
      }),
    );
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [this.#translate.instant('admin.users.items.statusChangeSuccess')],
    });
    this.#loadUserCatalogItems(userId).subscribe();
  }

  async #saveOrderItem(
    userId: string,
    itemId: string,
    body: UpdateStaffItemRequest,
  ): Promise<void> {
    await firstValueFrom(this.#itemService.updateStaffItem(itemId, body));
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [
        this.#translate.instant('admin.storageRequests.orderItemsEditSuccess'),
      ],
    });
    this.#loadUserCatalogItems(userId).subscribe();
  }

  async #deleteOrderCatalogItem(userId: string, itemId: string): Promise<void> {
    await firstValueFrom(this.#itemService.deleteStaffItem(itemId));
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [this.#translate.instant('admin.users.items.deleteSuccess')],
    });
    this.#loadUserCatalogItems(userId).subscribe();
  }

  async #resolveItemForModal(
    userId: string,
    itemId: string,
  ): Promise<AdminUserItemResponse | null> {
    const cached = this.userCatalogItems().find((item) => item.id === itemId);
    if (cached) {
      return cached;
    }

    await firstValueFrom(this.#loadUserCatalogItems(userId));
    const fromCatalog =
      this.userCatalogItems().find((item) => item.id === itemId) ?? null;
    if (fromCatalog) {
      return fromCatalog;
    }

    try {
      const item = await firstValueFrom(this.#itemService.getAdminItem(itemId));
      if (item.user_id !== userId) {
        return null;
      }
      return {
        ...item,
        in_active_order: true,
        allowed_next_statuses: [],
      };
    } catch {
      return null;
    }
  }

  #loadUserCatalogItems(userId: string): Observable<AdminUserItemResponse[]> {
    this.userCatalogItemsLoading.set(true);
    return this.#userAdmin.getUserItems(userId).pipe(
      switchMap((res) => {
        this.userCatalogItems.set(res.data);
        this.userCatalogItemsLoading.set(false);
        return of(res.data);
      }),
      catchError(() => {
        this.userCatalogItemsLoading.set(false);
        this.userCatalogItems.set([]);
        return of([]);
      }),
    );
  }

  async scanReceiveQr(): Promise<void> {
    const outcome = await this.#qrScan.scanQrCode();
    if (outcome.kind !== 'success') {
      if (outcome.kind === 'unsupportedEnvironment') {
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'info',
          text: [
            this.#translate.instant(
              'admin.storageRequests.receiveScanFallbackHint',
            ),
          ],
        });
      }
      return;
    }
    await this.#receiveFromRawCode(outcome.value);
  }

  submitManualReceiveCode(): void {
    void this.#receiveFromRawCode(this.receiveManualCode());
  }

  async scanHandoverQr(): Promise<void> {
    const outcome = await this.#qrScan.scanQrCode();
    if (outcome.kind !== 'success') {
      if (outcome.kind === 'unsupportedEnvironment') {
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'info',
          text: [
            this.#translate.instant(
              'admin.storageRequests.handoverScanFallbackHint',
            ),
          ],
        });
      }
      return;
    }
    await this.#handoverFromRawCode(outcome.value);
  }

  submitManualHandoverCode(): void {
    void this.#handoverFromRawCode(this.handoverManualCode());
  }

  async #handoverFromRawCode(raw: string): Promise<void> {
    const r = this.row();
    if (!r?.warehouse_id) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant('admin.storageRequests.handoverNoWarehouse'),
        ],
      });
      return;
    }

    const resolved = resolveOrderReceiveCode(raw, this.storage());
    if (!resolved.ok) {
      this.#toastHandoverCodeError(resolved.reason);
      return;
    }
    const parsed = resolved.parsed;

    const whName =
      this.receiveWarehouseInfo()?.name?.trim() ||
      this.#translate.instant('admin.storageRequests.receiveWarehouseUnknown');
    const kindKey =
      'box_id' in parsed
        ? 'admin.storageRequests.receiveKindBox'
        : 'admin.storageRequests.receiveKindItem';
    const shortId = shortIdForReceiveConfirm(parsed);
    const confirmed = await this.#dialog.confirm({
      title: this.#translate.instant(
        'admin.storageRequests.handoverConfirmTitle',
      ),
      message: this.#translate.instant(
        'admin.storageRequests.handoverConfirmMessage',
        {
          kind: this.#translate.instant(kindKey),
          id: shortId,
          warehouse: whName,
        },
      ),
      okLabel: this.#translate.instant(
        'admin.storageRequests.handoverConfirmOk',
      ),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }

    let handoverItems: ItemResponse[] = [];
    try {
      handoverItems = await resolveIntakeItemsForReceive(
        this.#itemService,
        this.#boxService,
        parsed,
      );
    } catch {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant('admin.storageRequests.handoverSaveError'),
        ],
      });
      return;
    }

    const orderSnapshot = r;
    this.savingMode.set('handover');
    this.#api
      .warehouseHandover(r.id, {
        warehouse_id: r.warehouse_id,
        ...('box_id' in parsed
          ? { box_id: parsed.box_id }
          : { item_id: parsed.item_id }),
      })
      .pipe(
        switchMap((handoverResponse) =>
          this.#api
            .getOrder(r.id)
            .pipe(map((updated) => ({ updated, handoverResponse }))),
        ),
      )
      .subscribe({
        next: ({ updated, handoverResponse }) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          this.handoverManualCode.set('');
          this.#loadInventory();
          const messages: string[] = [
            this.#translate.instant('admin.storageRequests.handoverSuccess'),
          ];
          if (handoverResponse.needs_dims_update) {
            messages.push(
              this.#translate.instant('admin.users.items.giveDimsUpdateHint'),
            );
          }
          if (
            updated.status === OrderStatuses.Completed ||
            (updated.status === OrderStatuses.InTransit &&
              this.deliveryComplete())
          ) {
            messages.push(
              this.#translate.instant(
                'admin.storageRequests.handoverAllCompleteHint',
              ),
            );
          }
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: messages,
          });
          void downloadReturnActForOrderHandover({
            actService: this.#returnAct,
            userAdmin: this.#userAdmin,
            warehouseService: this.#warehouseService,
            order: orderSnapshot,
            items: handoverItems,
            warehouse: this.receiveWarehouseInfo(),
            staffProfile: this.#profile.userInfo(),
          })
            .then(() => {
              this.#notification.addToQueue({
                id: `${Date.now()}-return-act`,
                type: 'success',
                text: [
                  this.#translate.instant(
                    'admin.users.items.returnActDownloaded',
                  ),
                ],
              });
            })
            .catch(() => undefined);
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err, 'admin.storageRequests.handoverSaveError');
        },
      });
  }

  #toastHandoverCodeError(reason: ResolveOrderReceiveCodeFailureReason): void {
    const keyMap: Record<ResolveOrderReceiveCodeFailureReason, string> = {
      empty: 'admin.storageRequests.handoverCodeEmpty',
      invalid: 'admin.storageRequests.receiveInvalidQr',
      too_short: 'admin.storageRequests.receiveCodeTooShort',
      not_in_order: 'admin.storageRequests.receiveCodeNotInOrder',
      ambiguous: 'admin.storageRequests.receiveCodeAmbiguous',
    };
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [this.#translate.instant(keyMap[reason])],
    });
  }

  async #receiveFromRawCode(raw: string): Promise<void> {
    const r = this.row();
    if (!r?.warehouse_id) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant('admin.storageRequests.receiveNoWarehouse'),
        ],
      });
      return;
    }

    if (this.receiveBlockedByUnverifiedProfile()) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            'admin.users.items.receiveProfileNotVerified',
          ),
        ],
      });
      return;
    }

    const resolved = resolveOrderReceiveCode(raw, this.storage());
    if (!resolved.ok) {
      this.#toastReceiveCodeError(resolved.reason);
      return;
    }
    const parsed = resolved.parsed;

    const whName =
      this.receiveWarehouseInfo()?.name?.trim() ||
      this.#translate.instant('admin.storageRequests.receiveWarehouseUnknown');
    const kindKey =
      'box_id' in parsed
        ? 'admin.storageRequests.receiveKindBox'
        : 'admin.storageRequests.receiveKindItem';
    const shortId = shortIdForReceiveConfirm(parsed);
    const confirmed = await this.#dialog.confirm({
      title: this.#translate.instant(
        'admin.storageRequests.receiveConfirmTitle',
      ),
      message: this.#translate.instant(
        'admin.storageRequests.receiveConfirmMessage',
        {
          kind: this.#translate.instant(kindKey),
          id: shortId,
          warehouse: whName,
        },
      ),
      okLabel: this.#translate.instant(
        'admin.storageRequests.receiveConfirmOk',
      ),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }

    let intakeItems: ItemResponse[] = [];
    try {
      intakeItems = await resolveIntakeItemsForReceive(
        this.#itemService,
        this.#boxService,
        parsed,
      );
    } catch {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant('admin.users.items.receiveError'),
        ],
      });
      return;
    }

    const orderSnapshot = r;
    this.savingMode.set('receive');
    this.#api
      .warehouseReceiveByCode(r.id, {
        warehouse_id: r.warehouse_id,
        code: raw.trim(),
      })
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          this.receiveManualCode.set('');
          this.#loadInventory(updated.id);
          const messages: string[] = [
            this.#translate.instant('admin.storageRequests.receiveSuccess'),
          ];
          if (updated.status === OrderStatuses.Stored) {
            messages.push(
              this.#translate.instant(
                'admin.storageRequests.receiveOrderAutoStored',
              ),
            );
          }
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: messages,
          });
          void downloadAcceptanceActForOrderIntake({
            actService: this.#acceptanceAct,
            userAdmin: this.#userAdmin,
            warehouseService: this.#warehouseService,
            order: orderSnapshot,
            items: intakeItems,
            warehouse: this.receiveWarehouseInfo(),
            staffProfile: this.#profile.userInfo(),
          })
            .then(() => {
              this.#notification.addToQueue({
                id: `${Date.now()}-act`,
                type: 'success',
                text: [
                  this.#translate.instant(
                    'admin.users.items.acceptanceActDownloaded',
                  ),
                ],
              });
            })
            .catch(() => undefined);
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastReceiveApiError(err);
        },
      });
  }

  #loadClientProfile(userId: string): void {
    this.clientProfileLoading.set(true);
    this.#userAdmin.getUserProfile(userId).subscribe({
      next: (profile) => {
        this.clientProfile.set(profile);
        this.clientProfileLoading.set(false);
      },
      error: () => {
        this.clientProfile.set(null);
        this.clientProfileLoading.set(false);
      },
    });
  }

  #toastReceiveApiError(err: HttpErrorResponse): void {
    const code = err.error?.code;
    if (code === 'PROFILE_NOT_VERIFIED') {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            'admin.users.items.receiveProfileNotVerified',
          ),
        ],
      });
      return;
    }
    if (typeof code === 'string' && this.#isReceiveCodeFailureReason(code)) {
      this.#toastReceiveCodeError(code);
      return;
    }
    this.#toastSaveError(err);
  }

  #isReceiveCodeFailureReason(
    value: string,
  ): value is ResolveOrderReceiveCodeFailureReason {
    return (
      value === 'empty' ||
      value === 'invalid' ||
      value === 'too_short' ||
      value === 'not_in_order' ||
      value === 'ambiguous'
    );
  }

  #toastReceiveCodeError(reason: ResolveOrderReceiveCodeFailureReason): void {
    const keyByReason: Record<ResolveOrderReceiveCodeFailureReason, string> = {
      empty: 'admin.storageRequests.receiveCodeEmpty',
      invalid: 'admin.storageRequests.receiveInvalidQr',
      too_short: 'admin.storageRequests.receiveCodeTooShort',
      not_in_order: 'admin.storageRequests.receiveCodeNotInOrder',
      ambiguous: 'admin.storageRequests.receiveCodeAmbiguous',
    };
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [this.#translate.instant(keyByReason[reason])],
    });
  }

  #loadReceiveWarehouseLabel(warehouseId: string): void {
    this.receiveWarehouseLoading.set(true);
    this.#warehouseService.getWarehouse(warehouseId).subscribe({
      next: (w) => {
        this.receiveWarehouseInfo.set(w);
        this.warehouseInfo.set(w);
        this.receiveWarehouseLoading.set(false);
      },
      error: () => {
        this.receiveWarehouseInfo.set(null);
        this.receiveWarehouseLoading.set(false);
      },
    });
  }

  #loadWarehouseInfo(warehouseId: string): void {
    this.warehouseLoading.set(true);
    this.#warehouseService.getWarehouse(warehouseId).subscribe({
      next: (w) => {
        this.warehouseInfo.set(w);
        this.warehouseLoading.set(false);
      },
      error: () => {
        this.warehouseInfo.set(null);
        this.warehouseLoading.set(false);
      },
    });
  }

  #patchStatus(status: string): void {
    this.#patchOrder(
      { status },
      'admin.storageRequests.statusSavedSuccess',
      false,
      'status',
    );
  }

  #completeOrder(): void {
    const r = this.row();
    if (!r) {
      return;
    }
    this.savingMode.set('status');
    this.#api
      .completeOrder(r.id)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.statusSavedSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  #cancelOrder(reason?: string): void {
    const r = this.row();
    if (!r) {
      return;
    }
    this.savingMode.set('status');
    this.#api
      .cancelOrderStaff(r.id, reason ? { reason } : {})
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.statusSavedSuccess',
              ),
            ],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  #applyOrderUpdate(updated: OrderResponse): void {
    this.row.set(updated);
    if (updated.warehouse_id) {
      this.#loadWarehouseInfo(updated.warehouse_id);
    } else {
      this.warehouseInfo.set(null);
    }
  }

  #patchOrder(
    body: PatchOrderRequest,
    successKey: string,
    clearNoteOnSuccess: boolean,
    mode: 'status' | 'note',
  ): void {
    const r = this.row();
    if (!r || Object.keys(body).length === 0) {
      return;
    }

    this.savingMode.set(mode);
    this.#api
      .patchOrder(r.id, body)
      .pipe(switchMap(() => this.#api.getOrder(r.id)))
      .subscribe({
        next: (updated) => {
          this.row.set(updated);
          if (clearNoteOnSuccess) {
            this.newNoteText.set('');
          }
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [this.#translate.instant(successKey)],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  createMoverBox(): void {
    const r = this.row();
    const sizeId = this.selectedBoxSizeId();
    if (!r?.user_id || !sizeId || this.saving()) {
      return;
    }
    this.savingMode.set('box');
    this.#boxService
      .createBox({
        user_id: r.user_id,
        box_size_id: sizeId,
        order_id: r.id,
        warehouse_id: r.warehouse_id,
      })
      .pipe(switchMap(() => this.#api.getOrderStorage(r.id)))
      .subscribe({
        next: (s) => {
          this.storage.set(s);
          if (s.boxes.length) {
            this.newItemBoxId.set(s.boxes[s.boxes.length - 1].box_id);
          }
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [this.#translate.instant('admin.moverInventory.boxCreated')],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  createWarehouseBoxAndPrintQr(): void {
    const r = this.row();
    const sizeId = this.selectedBoxSizeId();
    if (!r?.user_id || !r.warehouse_id || !sizeId || this.saving()) {
      return;
    }
    this.savingMode.set('box');
    this.#boxService
      .createBox({
        user_id: r.user_id,
        box_size_id: sizeId,
        order_id: r.id,
        warehouse_id: r.warehouse_id,
      })
      .pipe(
        switchMap((createdBox) =>
          this.#api
            .getOrderStorage(r.id)
            .pipe(map((storage) => ({ createdBoxId: createdBox.id, storage }))),
        ),
      )
      .subscribe({
        next: ({ createdBoxId, storage }) => {
          this.storage.set(storage);
          this.newItemBoxId.set(createdBoxId);
          this.receiveManualCode.set(createdBoxId);
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [this.#translate.instant('admin.moverInventory.boxCreated')],
          });
          void this.printMoverQr(createdBoxId, 'box');
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  addMoverItem(): void {
    const r = this.row();
    const boxId = this.newItemBoxId();
    const name = this.newItemName().trim();
    const photo = this.newItemPhoto();
    const isDisassembled = this.newItemIsDisassembled();
    const partsCount = Number(this.newItemPartsCount().trim());
    const quantity = normalizeItemQuantity(this.newItemQuantity());
    if (!r?.user_id || !boxId || !name || this.saving()) {
      return;
    }
    if (
      isDisassembled &&
      (!Number.isFinite(partsCount) ||
        partsCount < ITEM_MIN_PARTS_COUNT ||
        partsCount > ITEM_MAX_PARTS_COUNT)
    ) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            'CLIENT.CABINET.ITEM_CATALOG.VALIDATION_PARTS_COUNT_REQUIRED',
          ),
        ],
      });
      return;
    }
    this.savingMode.set('item');
    this.#boxService
      .addItemToBox(boxId, {
        user_id: r.user_id,
        name,
        ...(quantity > ITEM_MIN_QUANTITY ? { quantity } : {}),
        ...(isDisassembled
          ? { is_disassembled: true, parts_count: partsCount }
          : {}),
      })
      .pipe(
        switchMap((item: ItemResponse) => {
          if (photo) {
            return this.#photoUpload
              .uploadPhoto('item', item.id, photo)
              .pipe(switchMap(() => this.#maybeRegisterNewItemLine(item.id)));
          }
          return this.#maybeRegisterNewItemLine(item.id);
        }),
        switchMap(() => this.#api.getOrderStorage(r.id)),
      )
      .subscribe({
        next: (s) => {
          this.storage.set(s);
          this.newItemName.set('');
          this.newItemPhoto.set(null);
          this.newItemIsDisassembled.set(false);
          this.newItemPartsCount.set('');
          this.newItemQuantity.set('');
          this.savingMode.set('idle');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [this.#translate.instant('admin.moverInventory.itemAdded')],
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  async releaseMoverItem(itemId: string): Promise<void> {
    const r = this.row();
    if (!r || this.saving()) {
      return;
    }

    const maxQty = this.#resolveReleaseQuantityLimit(itemId);
    let quantity: number | undefined;
    if (maxQty > 1) {
      const prompt = await this.#dialog.prompt({
        title: this.#translate.instant('admin.moverInventory.releaseQuantityTitle'),
        message: this.#translate.instant(
          'admin.moverInventory.releaseQuantityMessage',
          { max: maxQty },
        ),
        inputPlaceholder: String(maxQty),
        inputValue: String(maxQty),
        okLabel: this.#translate.instant('admin.moverInventory.releaseItem'),
        cancelLabel: this.#translate.instant('common.actions.cancel'),
      });
      if (prompt.cancelled) {
        return;
      }
      const parsed = Number.parseInt(prompt.value.trim(), 10);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > maxQty) {
        await this.#dialog.alert({
          title: this.#translate.instant('admin.moverInventory.releaseQuantityTitle'),
          message: this.#translate.instant(
            'admin.moverInventory.releaseQuantityInvalid',
            { max: maxQty },
          ),
        });
        return;
      }
      if (parsed < maxQty) {
        quantity = parsed;
      }
    }

    this.savingMode.set('release');
    this.#api
      .moverReleaseItem(r.id, {
        item_id: itemId,
        ...(quantity != null ? { quantity } : {}),
      })
      .pipe(
        switchMap((releaseResponse) =>
          this.#api
            .getOrderStorage(r.id)
            .pipe(map((storage) => ({ storage, releaseResponse }))),
        ),
      )
      .subscribe({
        next: ({ storage, releaseResponse }) => {
          this.storage.set(storage);
          this.savingMode.set('idle');
          const messages = [
            this.#translate.instant('admin.moverInventory.itemReleased'),
          ];
          if (releaseResponse.needs_dims_update) {
            messages.push(
              this.#translate.instant('admin.users.items.giveDimsUpdateHint'),
            );
          }
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: messages,
          });
        },
        error: (err: HttpErrorResponse) => {
          this.savingMode.set('idle');
          this.#toastSaveError(err);
        },
      });
  }

  #resolveReleaseQuantityLimit(itemId: string): number {
    const storage = this.storage();
    if (!storage) {
      return 1;
    }
    let itemQty = 1;
    for (const box of storage.boxes) {
      const item = box.items.find((entry) => entry.id === itemId);
      if (item) {
        itemQty = (item.quantity ?? 0) > 0 ? (item.quantity ?? 1) : 1;
        break;
      }
    }
    const line = storage.lines.find((entry) => entry.item_id === itemId);
    if (!line) {
      return itemQty;
    }
    const lineRemaining = Math.max(
      0,
      (line.quantity ?? 1) - (line.fulfilled_quantity ?? 0),
    );
    return Math.min(itemQty, lineRemaining > 0 ? lineRemaining : itemQty);
  }

  onNewItemPhoto(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.newItemPhoto.set(file);
    input.value = '';
  }

  shortBoxId(id: string): string {
    if (id.length <= 12) {
      return id;
    }
    return `${id.slice(0, 8)}…`;
  }

  #maybeRegisterNewItemLine(itemId: string): Observable<{ message: string }> {
    const r = this.row();
    if (!r) {
      return of({ message: 'ok' });
    }
    if (r.type === OrderTypes.Delivery && r.return_to_warehouse) {
      return this.#api.moverRegisterOrderItem(r.id, { item_id: itemId });
    }
    return of({ message: 'ok' });
  }

  #loadOrderPhotos(orderId: string): void {
    this.orderPhotosLoading.set(true);
    this.#photoUpload.listOrderPhotos(orderId).subscribe({
      next: (res) => {
        const raw = res.data ?? [];
        const sorted = [...raw].sort((a, b) => {
          if (a.is_main !== b.is_main) {
            return a.is_main ? -1 : 1;
          }
          return a.sort_order - b.sort_order;
        });
        this.orderPhotos.set(sorted);
        this.orderPhotosLoading.set(false);
      },
      error: () => {
        this.orderPhotos.set([]);
        this.orderPhotosLoading.set(false);
      },
    });
  }

  #loadBoxSizes(): void {
    this.#boxSizes.getAdminBoxSizes().subscribe({
      next: (res) => {
        const active = res.data.filter((x) => x.is_active);
        this.boxSizeOptions.set(active);
        if (active.length && !this.selectedBoxSizeId()) {
          this.selectedBoxSizeId.set(active[0].id);
        }
      },
      error: () => {
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [
            this.#translate.instant('admin.moverInventory.boxSizesLoadError'),
          ],
        });
      },
    });
  }

  #loadInventory(orderId?: string): void {
    const r = this.row();
    if (!r) {
      return;
    }
    const expectedOrderId = orderId ?? r.id;
    this.inventoryLoading.set(true);
    this.#api.getOrderStorage(expectedOrderId).subscribe({
      next: (s) => {
        if (this.row()?.id !== expectedOrderId) {
          return;
        }

        this.storage.set(s);
        if (s.boxes.length && !this.newItemBoxId()) {
          this.newItemBoxId.set(s.boxes[0].box_id);
        }
        if (s.boxes.length && s.lines.length) {
          const defaultBoxId = s.boxes[0].box_id;
          const nextSelection: Record<string, string> = {
            ...this.moverLineBoxSelection(),
          };
          for (const line of s.lines) {
            if (line.item_id && !nextSelection[line.id]) {
              nextSelection[line.id] = defaultBoxId;
            }
          }
          this.moverLineBoxSelection.set(nextSelection);
        }
        this.inventoryLoading.set(false);
      },
      error: () => {
        if (this.row()?.id !== expectedOrderId) {
          return;
        }

        this.inventoryLoading.set(false);
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [
            this.#translate.instant('admin.moverInventory.inventoryLoadError'),
          ],
        });
      },
    });
  }

  #toastSaveError(err: HttpErrorResponse, fallbackKey?: string): void {
    const fallback = fallbackKey ?? 'admin.storageRequests.saveError';
    const msg =
      typeof err.error?.error === 'string'
        ? err.error.error
        : err.status === 403
          ? this.#translate.instant('admin.storageRequests.accessDenied')
          : this.#translate.instant(fallback);
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [msg],
    });
  }
}

function mapBackendOrderActions(
  operational: OrderOperationalContext,
): StaffOrderActionsResult {
  return {
    scenario: operational.scenario as StaffOrderOperationalScenarioId,
    operationalPhase: {
      id: operational.operational_phase.id as StaffOrderOperationalPhaseId,
      labelKey: operational.operational_phase.label_key,
    },
    readyToClose: operational.ready_to_close,
    blockers: mapBackendBlockers(operational.blockers ?? []),
    progress: mapBackendProgress(operational.progress),
    supportedActions: mapBackendSupportedActions(
      operational.supported_actions ?? [],
    ),
    byRole: mapBackendActionsByRole(operational.by_role),
    actionGroupsByRole: mapBackendActionGroupsByRole(
      operational.action_groups_by_role,
    ),
    primaryNextByRole: mapBackendPrimaryActionsByRole(
      operational.primary_next_by_role,
    ),
    cockpitActionGroups: mapBackendActionGroups(
      operational.cockpit_action_groups,
    ),
    cockpitPrimaryAction: mapBackendSupportedAction(
      operational.cockpit_primary_action,
    ),
  };
}

function mapBackendActionsByRole(
  byRole: OrderOperationalContext['by_role'],
): StaffOrderActionsResult['byRole'] {
  if (!byRole) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(byRole).map(([role, actions]) => [
      role,
      mapBackendSupportedActions(actions ?? []),
    ]),
  ) as StaffOrderActionsResult['byRole'];
}

function mapBackendPrimaryActionsByRole(
  byRole: OrderOperationalContext['primary_next_by_role'],
): StaffOrderActionsResult['primaryNextByRole'] {
  if (!byRole) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(byRole).map(([role, action]) => [
      role,
      mapBackendSupportedAction(action),
    ]),
  ) as StaffOrderActionsResult['primaryNextByRole'];
}

function mapBackendSupportedActions(
  actions: OrderSupportedAction[],
): StaffOrderSupportedAction[] {
  return actions.reduce<StaffOrderSupportedAction[]>((acc, action) => {
    const mapped = mapBackendSupportedAction(action);
    if (mapped) {
      acc.push(mapped);
    }
    return acc;
  }, []);
}

function mapBackendActionGroupsByRole(
  byRole: OrderOperationalContext['action_groups_by_role'],
): StaffOrderActionsResult['actionGroupsByRole'] {
  if (!byRole) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(byRole).map(([role, groups]) => [
      role,
      mapBackendActionGroups(groups) ?? {
        primary: [],
        secondary: [],
        destructive: [],
      },
    ]),
  ) as StaffOrderActionsResult['actionGroupsByRole'];
}

function mapBackendActionGroups(
  groups: OrderOperationalContext['cockpit_action_groups'] | undefined,
): StaffOrderActionGroups | null {
  if (!groups) {
    return null;
  }
  return {
    primary: mapBackendSupportedActions(groups.primary ?? []),
    secondary: mapBackendSupportedActions(groups.secondary ?? []),
    destructive: mapBackendSupportedActions(groups.destructive ?? []),
  };
}

function mapBackendBlockers(
  blockers: NonNullable<OrderOperationalContext['blockers']>,
): StaffOrderOperationalBlocker[] {
  return blockers.map((blocker) => ({
    code: blocker.code,
    scope: blocker.scope,
    role: blocker.role,
    actionId: blocker.action_id as StaffOrderActionId | undefined,
    hintKey: blocker.hint_key,
    message: blocker.message,
    current: blocker.current,
    total: blocker.total,
  }));
}

function mapBackendProgress(
  progress: OrderOperationalContext['progress'],
): StaffOrderOperationalProgress {
  return {
    scenario: progress.scenario as StaffOrderOperationalScenarioId,
    currentPhaseStep: progress.current_phase_step,
    totalPhaseSteps: progress.total_phase_steps,
    phaseCompletionPercent: progress.phase_completion_percent,
    moverAccept: progress.mover_accept
      ? {
          accepted: progress.mover_accept.accepted ?? 0,
          total: progress.mover_accept.total,
        }
      : null,
    warehouseReceive: progress.warehouse_receive
      ? {
          received: progress.warehouse_receive.received ?? 0,
          total: progress.warehouse_receive.total,
        }
      : null,
    warehouseHandover: progress.warehouse_handover
      ? {
          received: progress.warehouse_handover.received ?? 0,
          total: progress.warehouse_handover.total,
        }
      : null,
    deliveryComplete: progress.delivery_complete,
    storage: progress.storage
      ? {
          totalUnits: progress.storage.total_units,
          storedUnits: progress.storage.stored_units,
          deliveredUnits: progress.storage.delivered_units,
        }
      : null,
  };
}

function mapBackendSupportedAction(
  action: OrderSupportedAction | null | undefined,
): StaffOrderSupportedAction | null {
  if (!action) {
    return null;
  }

  const mapped: StaffOrderSupportedAction = {
    id: action.id as StaffOrderActionId,
    role: action.role as StaffOrderStaffRole,
    kind: action.kind as StaffOrderActionKind,
    available: action.available,
    labelKey: action.label_key,
  };
  if (action.block_reason) {
    mapped.blockReason = action.block_reason as StaffOrderActionBlockReason;
  }
  if (action.blocked_hint_key) {
    mapped.blockedHintKey = action.blocked_hint_key;
  }
  return mapped;
}

function shortIdForReceiveConfirm(
  parsed: { box_id: string } | { item_id: string },
): string {
  const id = 'box_id' in parsed ? parsed.box_id : parsed.item_id;
  if (id.length <= 13) {
    return id;
  }
  return `${id.slice(0, 8)}…`;
}
